import { Badge, Card, Icon, type BadgeTone } from '@astral/ui';
import { readStickerData, type ProjectDTO, type ProjectStatus } from '@astral/shared';
import { useI18n, useT, type TranslationKey } from '../providers/I18nProvider.js';
import { formatRelative } from '../lib/format.js';

const STATUS_TONE: Record<ProjectStatus, BadgeTone> = {
  DRAFT: 'neutral',
  ACTIVE: 'success',
  ARCHIVED: 'warning',
};

export interface ProjectCardProps {
  project: ProjectDTO;
  onOpen: () => void;
  onMore: () => void;
}

/** A rich project card: opens on tap, with a "more" menu for actions. */
export function ProjectCard({ project, onOpen, onMore }: ProjectCardProps): JSX.Element {
  const t = useT();
  const { language } = useI18n();
  const timestamp = project.lastAutosaveAt ?? project.updatedAt;
  const stickerCount = readStickerData(project.data).assets.length;

  return (
    <Card padding="md" interactive className="project-card border-glow" onClick={onOpen}>
      <div className="project-card__head">
        <div className="project-card__title">{project.title}</div>
        <div className="project-card__head-actions">
          <Badge tone={STATUS_TONE[project.status]}>
            {t(`projects.status.${project.status}` as TranslationKey)}
          </Badge>
          <button
            className="icon-btn icon-btn--sm"
            aria-label={t('projects.more')}
            onClick={(event) => {
              event.stopPropagation();
              onMore();
            }}
          >
            <Icon name="more" size={18} />
          </button>
        </div>
      </div>

      {project.description && <p className="project-card__desc">{project.description}</p>}

      {project.tags.length > 0 && (
        <div className="project-card__tags">
          {project.tags.map((tag) => (
            <Badge key={tag} tone="accent">
              {tag}
            </Badge>
          ))}
        </div>
      )}

      <div className="project-card__meta">
        <span className="project-card__meta-item">
          <Icon name="image" size={14} /> {t('stickers.count', { count: stickerCount })}
        </span>
        <span>{t('projects.lastEdited', { date: formatRelative(timestamp, language) })}</span>
      </div>
    </Card>
  );
}
